/**
 * SubscribeToVideoCallMessageUseCase
 *
 * Domain subscription to the single `videoCall` chat message, surfacing each
 * `modified` change as a `ChatMessageEntity` so the page can drive the 1:1
 * call lifecycle:
 *   - the caller sees the audience joined once `audienceStreamingId` is set
 *     (`video_call_screen.dart:364-365`);
 *   - the audience tears down once `isLive` flips to `false`
 *     (`video_call_screen.dart:375-381`).
 *
 * Flutter source: lib/presentation/screens/chat/video_call_screen.dart:347-404
 *   (`_subscribeToVideoCallMessage`). The `change.type == DocumentChangeType
 *   .modified` gate (`:361`) and the `ChatMessage.fromJson` parse (`:362-363`)
 *   are applied in `VideoCallService.subscribeToVideoCallMessage`, so this use
 *   case only forwards the channel coordinates and the callbacks.
 *
 * Two callbacks are forwarded:
 *   - `onModified` — the lifecycle-reaction path, `modified`-only (parity `:361`).
 *   - `onLatest` — the message-resolution path, fired for every change type so
 *     the consumer's call-message ref tracks the latest doc state (the web
 *     analog of the chat screen's `_listItems`, fed by all change types,
 *     `chat_screen.dart:507/582/608`). The three write-back use cases resolve
 *     their target message from this ref; without it the create-time `added`
 *     change is invisible and every write-back no-ops (business-parity finding 1).
 *
 * The Flutter `latestLiveGiftInfo` branch (`:383-400`) is intentionally NOT
 * ported — it is Phase 2 (task-prompt line 30).
 *
 * Pure-domain contract: the use case forwards its arguments unchanged and
 * returns the service's `Unsubscribe` handle; lifecycle/cleanup is a
 * presentation concern (the Task-10 `useVideoCall` hook).
 */

import type { Unsubscribe } from 'firebase/firestore';
import { VideoCallService, videoCallService } from '@/domain/services/videoCall/videoCallService';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';

export interface SubscribeToVideoCallMessageParams {
  /** The chat node id (`chatUsersInfo.groupChatId`). */
  groupChatId: string;
  /** The call message's `videoCallId`. */
  callMessageId: string;
  /**
   * Invoked with the remapped `ChatMessageEntity` for each `modified` change
   * only (parity with `video_call_screen.dart:361`) — the lifecycle-reaction
   * path.
   */
  onModified: (message: ChatMessageEntity) => void;
  /**
   * Invoked with the remapped `ChatMessageEntity` for EVERY change type
   * (`added` / `modified` / `removed`) — the message-resolution path. The
   * consumer keeps its call-message ref current from here (the web analog of
   * Flutter's `_listItems`). Optional.
   */
  onLatest?: (message: ChatMessageEntity) => void;
  /** Optional error sink for the underlying snapshot listener. */
  onError?: (error: Error) => void;
}

export class SubscribeToVideoCallMessageUseCase {
  private videoCallService: VideoCallService;

  constructor(service: VideoCallService = videoCallService) {
    this.videoCallService = service;
  }

  /**
   * Subscribe to the call message and forward both the `modified`-only
   * lifecycle-reaction changes (`onModified`) and the every-change-type
   * resolution updates (`onLatest`) as entities.
   *
   * @returns The `Unsubscribe` handle; the caller is responsible for cleanup.
   */
  execute({
    groupChatId,
    callMessageId,
    onModified,
    onLatest,
    onError,
  }: SubscribeToVideoCallMessageParams): Unsubscribe {
    return this.videoCallService.subscribeToVideoCallMessage(
      groupChatId,
      callMessageId,
      onModified,
      onLatest,
      onError,
    );
  }
}

// Export singleton instance
export const subscribeToVideoCallMessageUseCase = new SubscribeToVideoCallMessageUseCase();
